import { useEffect, useState } from "react";

import { useConversationReadMarks } from "~/ai-chat/data-source/conversation-read-store";
import { flattenConversations, useConversations } from "~/ai-chat/data-source/use-conversations";
import { SavedChatShareModal } from "~/ai-chat/feature/chat-share-modal";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { useArchiveChat } from "~/ai-chat/feature/use-archive-chat";
import { useRenameChat } from "~/ai-chat/feature/use-rename-chat";
import type { ConversationGroupBy, ConversationStatusFilter } from "~/ai-chat/model/conversation";
import { ConversationList } from "~/ai-chat/ui/conversation-list";

/**
 * Chat-history sidebar: lists the user's conversations, selects one, and starts a
 * fresh chat. Loading the selected conversation into the transcript is handled by
 * `useHydrateActiveConversation` (mounted in the page/widget shell) so switching
 * works even with this list closed.
 */
export function AiChatHistorySidebar({ onAfterSelect }: { onAfterSelect?: () => void }) {
  const {
    activeConversationId,
    setActiveConversationId,
    setChatTitle,
    startNewConversation,
    markConversationSynced,
    runningConversationIds,
  } = useAiChat();
  const readMarks = useConversationReadMarks();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ConversationStatusFilter>("active");
  const [groupBy, setGroupBy] = useState<ConversationGroupBy>("date");
  const [shareTarget, setShareTarget] = useState<{ conversationId: string; resourceId: string } | null>(null);
  // Debounce so a search doesn't fire (and cache) a query per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  // "active" hides archived; "all" includes them. (Archived-only needs a backend flag.)
  const conversationsQuery = useConversations({ q: debouncedSearch, includeArchived: statusFilter === "all" });
  const { rename } = useRenameChat();
  const { archive } = useArchiveChat();

  const conversations = flattenConversations(conversationsQuery.data);

  const select = (id: string) => {
    // Switching away replaces the local transcript, so the departing
    // conversation's unsynced protection no longer guards anything real —
    // without releasing it, returning to a conversation whose persistence
    // failed would show the *other* conversation's messages under its id.
    if (activeConversationId !== null && activeConversationId !== id) {
      markConversationSynced(activeConversationId);
    }
    setActiveConversationId(id);
    setChatTitle(conversations.find((conversation) => conversation.id === id)?.title ?? null);
    onAfterSelect?.();
  };

  const startNew = () => {
    startNewConversation();
    onAfterSelect?.();
  };

  // Opens the dialog over the list without selecting the row: selecting closes the panel's history view,
  // which would unmount the dialog with it.
  const share = (id: string) => {
    const resourceId = conversations.find((conversation) => conversation.id === id)?.transcriptResourceId;
    if (resourceId) setShareTarget({ conversationId: id, resourceId });
  };

  return (
    <>
      <ConversationList
        conversations={conversations}
        activeId={activeConversationId}
        onSelect={select}
        onNew={startNew}
        onRename={rename}
        onArchive={archive}
        onShare={share}
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        onLoadMore={() => void conversationsQuery.fetchNextPage()}
        hasMore={conversationsQuery.hasNextPage}
        isFetchingMore={conversationsQuery.isFetchingNextPage}
        isLoading={conversationsQuery.isLoading}
        loadFailed={conversationsQuery.isError}
        // A failed "Load more" is retried as the next page; refetch() would only reload the pages already shown.
        onRetry={() =>
          void (conversationsQuery.isFetchNextPageError
            ? conversationsQuery.fetchNextPage()
            : conversationsQuery.refetch())
        }
        runningIds={runningConversationIds}
        readMarks={readMarks}
      />
      {shareTarget ? (
        <SavedChatShareModal
          conversationId={shareTarget.conversationId}
          resourceId={shareTarget.resourceId}
          onClose={() => setShareTarget(null)}
        />
      ) : null}
    </>
  );
}
